import {
  UserAccount,
  UserIdentity,
  PasskeyCredential,
  UserSession,
  AccountSecurityEvent,
  FinancialScenario,
  UserFavoriteItem,
  GlobalAlert,
  NotificationItem,
  NotificationPreference,
  PrivacyPreference,
  ConsentRecord,
  RoleAssignment,
  AdminAuditLogEntry,
  IdentityTelemetry,
  AuthProviderType,
  SavedCalculation,
  AppView,
  InternalRole,
  YamooPayActivityItem,
} from '../types';
import {
  INITIAL_MOCK_USER,
  INITIAL_MOCK_IDENTITIES,
  INITIAL_MOCK_PASSKEYS,
  INITIAL_MOCK_SESSIONS,
  INITIAL_SECURITY_EVENTS,
  INITIAL_FINANCIAL_SCENARIOS,
  INITIAL_FAVORITES,
  INITIAL_ALERTS,
  INITIAL_NOTIFICATIONS,
  INITIAL_NOTIFICATION_PREFERENCES,
  INITIAL_PRIVACY_PREFERENCES,
  INITIAL_CONSENT_RECORDS,
  INITIAL_ROLE_ASSIGNMENTS,
  INITIAL_ADMIN_AUDIT_LOGS,
  INITIAL_TELEMETRY,
  INITIAL_ACTIVITIES,
} from '../data/identityData';
import { authEngine, AuthIdentity } from './auth/authEngine';

export interface IdentityState {
  user: UserAccount;
  identities: UserIdentity[];
  passkeys: PasskeyCredential[];
  sessions: UserSession[];
  securityEvents: AccountSecurityEvent[];
  activities: YamooPayActivityItem[];
  scenarios: FinancialScenario[];
  savedCalculations: SavedCalculation[];
  favorites: UserFavoriteItem[];
  alerts: GlobalAlert[];
  notifications: NotificationItem[];
  notificationPreferences: NotificationPreference;
  privacyPreferences: PrivacyPreference;
  consentRecords: ConsentRecord[];
  roleAssignments: RoleAssignment[];
  adminAuditLogs: AdminAuditLogEntry[];
  telemetry: IdentityTelemetry;
  sudoModeUntil?: number; // timestamp until sudo mode expires
}

let globalIdentityState: IdentityState = {
  user: authEngine.getCurrentUser() || { ...INITIAL_MOCK_USER },
  identities: [...INITIAL_MOCK_IDENTITIES],
  passkeys: [...INITIAL_MOCK_PASSKEYS],
  sessions: [...INITIAL_MOCK_SESSIONS],
  securityEvents: [...INITIAL_SECURITY_EVENTS],
  activities: [...INITIAL_ACTIVITIES],
  scenarios: [...INITIAL_FINANCIAL_SCENARIOS],
  savedCalculations: [
    {
      id: 'calc_sal_01',
      title: 'Senior Staff Engineer — California $220,000 W-2',
      toolId: 'salary-engine',
      toolName: 'Salary & Paycheck Calculator',
      category: 'Income & Tax',
      market: 'US / CA',
      primaryResult: '$12,410 / month',
      subtitle: 'California $220,000 W-2',
      inputsSummary: 'Single filer • Standard Deduction • 2026 IRS',
      region: 'US',
      timestamp: 1787133600000,
      createdAt: '2026-08-20T10:00:00Z',
      summary: 'Net Monthly: $12,410 | Federal Tax: $38,420 | CA State Tax: $16,840 | Social: $11,920',
      canCompare: true,
    },
    {
      id: 'calc_loan_01',
      title: 'SF Bay Area Mortgage — $750,000 Purchase / 20% Down / 6.25%',
      toolId: 'loan-engine',
      toolName: 'Mortgage & Loan Calculator',
      category: 'Borrow',
      market: 'US / CA',
      primaryResult: '$3,694.20 / month',
      subtitle: '$750,000 Purchase / 20% Down',
      inputsSummary: '30-Year Fixed • Monthly Compounding • PITI',
      region: 'US',
      timestamp: 1787323800000,
      createdAt: '2026-08-22T14:30:00Z',
      summary: 'P&I Monthly: $3,694.20 | PITI Housing Total: $4,820.00/mo | APR: 6.38%',
      canCompare: true,
    },
    {
      id: 'calc_trf_01',
      title: 'US → India Remittance $2,000 USD → INR',
      toolId: 'money-transfers',
      toolName: 'Money Transfer Comparison',
      category: 'Currency',
      market: 'US / IN',
      primaryResult: '₹167,420 INR',
      subtitle: '$2,000 USD → INR',
      inputsSummary: 'Mid-Market Rate: 83.71 INR per USD',
      region: 'Global',
      timestamp: 1786958100000,
      createdAt: '2026-08-18T09:15:00Z',
      summary: 'Wise: ₹167,420 (Fee: $5.20) | Remitly: ₹166,800 (Fee: $0.00)',
      canCompare: true,
    },
    {
      id: 'calc_curr_02',
      title: 'EUR → USD Spot Conversion Quote',
      toolId: 'currency-engine',
      toolName: 'Currency Converter',
      category: 'Currency',
      market: 'USD/EUR',
      primaryResult: 'USD 500 → EUR 460.50',
      subtitle: 'EUR 1,000 → USD 1,085.00',
      inputsSummary: 'Rate: 1.0850 • Real-time ECB Mid-Market',
      region: 'EU',
      timestamp: 1787400000000,
      createdAt: '2026-08-23T11:20:00Z',
      summary: 'Spot Mid-Market: 1.0850 | Interbank spread savings: $14.50',
      canCompare: false,
    },
    {
      id: 'calc_wealth_01',
      title: '10-Year Compound Savings & Growth Plan',
      toolId: 'wealth-engine',
      toolName: 'Compound Wealth & Savings Calculator',
      category: 'Save & Invest',
      market: 'Global',
      primaryResult: '$482,900 Future Value',
      subtitle: '$1,000/mo contribution @ 7% return',
      inputsSummary: '7% Nominal Return • 2.5% Inflation • 10-Yr Horizon',
      region: 'Global',
      timestamp: 1787200000000,
      createdAt: '2026-08-21T16:00:00Z',
      summary: 'Principal: $120,000 | Total Growth: $362,900',
      canCompare: true,
    },
    {
      id: 'calc_everyday_01',
      title: 'Freelance Invoice VAT & Sales Tax (20% TVA)',
      toolId: 'everyday-calculator',
      toolName: 'Everyday Tax & Tip Engine',
      category: 'Everyday',
      market: 'EU / FR',
      primaryResult: '1,200 € HT + 240 € TVA',
      subtitle: 'Total TTC: 1,440.00 €',
      inputsSummary: 'Base: 1,200 € • Rate: 20% Standard TVA',
      region: 'FR',
      timestamp: 1787050000000,
      createdAt: '2026-08-19T13:40:00Z',
      summary: 'HT: 1,200 € | TVA: 240 € | Total TTC: 1,440 €',
      canCompare: false,
    },
  ],
  favorites: [...INITIAL_FAVORITES],
  alerts: [...INITIAL_ALERTS],
  notifications: [...INITIAL_NOTIFICATIONS],
  notificationPreferences: { ...INITIAL_NOTIFICATION_PREFERENCES },
  privacyPreferences: { ...INITIAL_PRIVACY_PREFERENCES },
  consentRecords: [...INITIAL_CONSENT_RECORDS],
  roleAssignments: [...INITIAL_ROLE_ASSIGNMENTS],
  adminAuditLogs: [...INITIAL_ADMIN_AUDIT_LOGS],
  telemetry: { ...INITIAL_TELEMETRY },
};

// Sync authEngine updates
authEngine.subscribe(() => {
  const currentUser = authEngine.getCurrentUser();
  if (currentUser) {
    globalIdentityState = {
      ...globalIdentityState,
      user: currentUser,
      identities: authEngine.getUserIdentities(currentUser.id).map((i) => ({
        id: i.id,
        userId: i.userId,
        provider: i.provider,
        providerSubject: i.providerSubject,
        email: i.emailAtProvider,
        linkedAt: i.createdAt,
        lastUsedAt: i.lastUsedAt,
      })),
      passkeys: authEngine.getUserPasskeys(currentUser.id),
      sessions: authEngine.getUserSessions(currentUser.id),
      securityEvents: authEngine.getSecurityEvents(currentUser.id),
    };
  } else {
    globalIdentityState = {
      ...globalIdentityState,
      user: {
        ...globalIdentityState.user,
        isGuest: true,
      },
    };
  }
  notifyListeners();
});

// Listeners for state reactivity
type IdentityStateListener = (state: IdentityState) => void;
const listeners: Set<IdentityStateListener> = new Set();

export function getIdentityState(): IdentityState {
  return globalIdentityState;
}

export function subscribeIdentityState(listener: IdentityStateListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const identityEngine = {
  getState: getIdentityState,
  subscribe: subscribeIdentityState,
  logout,
  loginWithProvider,
  registerPasskey,
  revokePasskey: removePasskey,
  removePasskey,
  revokeSession,
  revokeAllOtherSessions,
};

function notifyListeners() {
  listeners.forEach((l) => l(globalIdentityState));
}

// -------------------------------------------------------------
// AUTHENTICATION & PASSKEYS
// -------------------------------------------------------------

export async function registerPasskey(name: string): Promise<PasskeyCredential> {
  const newPasskey: PasskeyCredential = {
    id: `pk_${Date.now()}`,
    userId: globalIdentityState.user.id,
    credentialId: `cred_fido2_${Math.random().toString(36).substring(2, 12)}`,
    name: name.trim() || 'MacBook Touch ID / FIDO Key',
    publicKeySummary: 'ECDSA_P256_FIDO2_VERIFIED',
    createdAt: new Date().toISOString(),
    lastUsedAt: new Date().toISOString(),
    transports: ['internal', 'hybrid'],
    status: 'ACTIVE',
  };

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'PASSKEY_ADDED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: `Registered passkey authenticator: "${newPasskey.name}"`,
  };

  globalIdentityState = {
    ...globalIdentityState,
    passkeys: [newPasskey, ...globalIdentityState.passkeys],
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
    telemetry: {
      ...globalIdentityState.telemetry,
      passkeyAdoptionPct: Math.min(100, globalIdentityState.telemetry.passkeyAdoptionPct + 0.5),
    },
  };

  notifyListeners();
  return newPasskey;
}

export function removePasskey(passkeyId: string): void {
  const target = globalIdentityState.passkeys.find((p) => p.id === passkeyId);
  if (!target) return;

  const updatedPasskeys = globalIdentityState.passkeys.filter((p) => p.id !== passkeyId);

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'PASSKEY_REMOVED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: `Removed passkey: "${target.name}"`,
  };

  globalIdentityState = {
    ...globalIdentityState,
    passkeys: updatedPasskeys,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
}

export function loginWithProvider(provider: AuthProviderType, emailInput?: string): UserAccount {
  const updatedUser: UserAccount = {
    ...globalIdentityState.user,
    isGuest: false,
    primaryEmail: emailInput || globalIdentityState.user.primaryEmail,
    updatedAt: new Date().toISOString(),
  };

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: updatedUser.id,
    eventType: 'SIGN_IN',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: `Successfully signed in via ${provider} (${updatedUser.primaryEmail})`,
  };

  globalIdentityState = {
    ...globalIdentityState,
    user: updatedUser,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
  return updatedUser;
}

export function updateUserProfile(updates: Partial<UserAccount>): UserAccount {
  const updatedUser: UserAccount = {
    ...globalIdentityState.user,
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: updatedUser.id,
    eventType: 'PROFILE_UPDATED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: 'Updated account profile details and locale preferences',
  };

  globalIdentityState = {
    ...globalIdentityState,
    user: updatedUser,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
  return updatedUser;
}

export function logout(): void {
  globalIdentityState = {
    ...globalIdentityState,
    user: {
      ...globalIdentityState.user,
      isGuest: true,
    },
  };
  notifyListeners();
}

// -------------------------------------------------------------
// SESSIONS & STEP-UP SUDO MODE
// -------------------------------------------------------------

export function revokeSession(sessionId: string): void {
  const updatedSessions = globalIdentityState.sessions.filter((s) => s.id !== sessionId);

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'SESSION_REVOKED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: `Revoked active session ID: ${sessionId}`,
  };

  globalIdentityState = {
    ...globalIdentityState,
    sessions: updatedSessions,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
}

export function revokeAllOtherSessions(): void {
  const currentSession = globalIdentityState.sessions.filter((s) => s.isCurrent);

  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'SESSION_REVOKED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: 'Revoked all other active sessions globally',
  };

  globalIdentityState = {
    ...globalIdentityState,
    sessions: currentSession,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
}

export function isSudoActive(): boolean {
  if (!globalIdentityState.sudoModeUntil) return false;
  return Date.now() < globalIdentityState.sudoModeUntil;
}

export function activateSudoMode(): void {
  const sudoDurationMs = 15 * 60 * 1000; // 15 minutes window
  globalIdentityState = {
    ...globalIdentityState,
    sudoModeUntil: Date.now() + sudoDurationMs,
  };
  notifyListeners();
}

// -------------------------------------------------------------
// SAVED CALCULATIONS & SCENARIOS
// -------------------------------------------------------------

export function saveCalculationToAccount(calc: Omit<SavedCalculation, 'id' | 'createdAt'>): SavedCalculation {
  const newCalc: SavedCalculation = {
    ...calc,
    id: `calc_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    createdAt: new Date().toISOString(),
  };

  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: [newCalc, ...globalIdentityState.savedCalculations],
  };

  notifyListeners();
  return newCalc;
}

export function createFinancialScenario(
  title: string,
  category: FinancialScenario['category'],
  description?: string,
  initialCalculations: SavedCalculation[] = []
): FinancialScenario {
  const newScenario: FinancialScenario = {
    id: `scen_${Date.now()}`,
    userId: globalIdentityState.user.id,
    title,
    description,
    category,
    items: initialCalculations,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  globalIdentityState = {
    ...globalIdentityState,
    scenarios: [newScenario, ...globalIdentityState.scenarios],
  };

  notifyListeners();
  return newScenario;
}

export function deleteFinancialScenario(scenarioId: string): void {
  globalIdentityState = {
    ...globalIdentityState,
    scenarios: globalIdentityState.scenarios.filter((s) => s.id !== scenarioId),
  };
  notifyListeners();
}

// -------------------------------------------------------------
// FAVORITES, ALERTS & NOTIFICATIONS
// -------------------------------------------------------------

export function toggleFavorite(
  type: UserFavoriteItem['type'],
  targetId: string,
  label: string,
  sublabel: string
): void {
  const existingIndex = globalIdentityState.favorites.findIndex(
    (f) => f.type === type && f.targetId === targetId
  );

  let updatedFavs = [...globalIdentityState.favorites];
  if (existingIndex >= 0) {
    updatedFavs.splice(existingIndex, 1);
  } else {
    updatedFavs.unshift({
      id: `fav_${Date.now()}`,
      userId: globalIdentityState.user.id,
      type,
      targetId,
      label,
      sublabel,
      addedAt: new Date().toISOString(),
    });
  }

  globalIdentityState = {
    ...globalIdentityState,
    favorites: updatedFavs,
  };

  notifyListeners();
}

export function createGlobalAlert(
  type: GlobalAlert['type'],
  targetPairOrCorridor: string,
  targetThreshold: number,
  currentVal: number,
  channel: GlobalAlert['channel'] = 'IN_APP'
): GlobalAlert {
  const newAlert: GlobalAlert = {
    id: `alert_${Date.now()}`,
    userId: globalIdentityState.user.id,
    type,
    targetPairOrCorridor,
    targetThreshold,
    currentVal,
    condition: currentVal < targetThreshold ? 'ABOVE' : 'BELOW',
    status: 'ACTIVE',
    channel,
    createdAt: new Date().toISOString(),
    lastEvaluatedAt: new Date().toISOString(),
  };

  globalIdentityState = {
    ...globalIdentityState,
    alerts: [newAlert, ...globalIdentityState.alerts],
  };

  notifyListeners();
  return newAlert;
}

export function toggleAlertStatus(alertId: string): void {
  const updated = globalIdentityState.alerts.map((a) => {
    if (a.id === alertId) {
      return {
        ...a,
        status: a.status === 'ACTIVE' ? ('PAUSED' as const) : ('ACTIVE' as const),
      };
    }
    return a;
  });

  globalIdentityState = { ...globalIdentityState, alerts: updated };
  notifyListeners();
}

export function deleteGlobalAlert(alertId: string): void {
  globalIdentityState = {
    ...globalIdentityState,
    alerts: globalIdentityState.alerts.filter((a) => a.id !== alertId),
  };
  notifyListeners();
}

export function markNotificationRead(notifId: string): void {
  const updated = globalIdentityState.notifications.map((n) =>
    n.id === notifId ? { ...n, read: true } : n
  );
  globalIdentityState = { ...globalIdentityState, notifications: updated };
  notifyListeners();
}

export function markAllNotificationsRead(): void {
  const updated = globalIdentityState.notifications.map((n) => ({ ...n, read: true }));
  globalIdentityState = { ...globalIdentityState, notifications: updated };
  notifyListeners();
}

export function updateNotificationPreferences(prefs: Partial<NotificationPreference>): void {
  globalIdentityState = {
    ...globalIdentityState,
    notificationPreferences: {
      ...globalIdentityState.notificationPreferences,
      ...prefs,
    },
  };
  notifyListeners();
}

// -------------------------------------------------------------
// PRIVACY, CONSENT & DATA EXPORT / DELETION
// -------------------------------------------------------------

export function updatePrivacyPreferences(prefs: Partial<PrivacyPreference>): void {
  globalIdentityState = {
    ...globalIdentityState,
    privacyPreferences: {
      ...globalIdentityState.privacyPreferences,
      ...prefs,
    },
  };
  notifyListeners();
}

export function exportUserDataJSON(): void {
  const dataPayload = {
    user: globalIdentityState.user,
    identities: globalIdentityState.identities,
    passkeys: globalIdentityState.passkeys.map((p) => ({
      id: p.id,
      name: p.name,
      createdAt: p.createdAt,
      lastUsedAt: p.lastUsedAt,
      status: p.status,
    })),
    savedCalculations: globalIdentityState.savedCalculations,
    scenarios: globalIdentityState.scenarios,
    favorites: globalIdentityState.favorites,
    alerts: globalIdentityState.alerts,
    securityEvents: globalIdentityState.securityEvents,
    consentRecords: globalIdentityState.consentRecords,
    privacyPreferences: globalIdentityState.privacyPreferences,
    exportedAt: new Date().toISOString(),
    platform: 'YamooPay Global Identity & Personal Workspace',
  };

  const jsonStr = JSON.stringify(dataPayload, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `yamoopay-my-data-export-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();

  // Log security audit event
  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'DATA_EXPORT_REQUESTED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: 'User downloaded full machine-readable JSON data archive (GDPR / CCPA)',
  };

  globalIdentityState = {
    ...globalIdentityState,
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
}

export function deleteAccountData(): void {
  const securityEvent: AccountSecurityEvent = {
    id: `evt_${Date.now()}`,
    userId: globalIdentityState.user.id,
    eventType: 'ACCOUNT_DELETION_REQUESTED',
    ip: '198.51.100.42',
    location: 'San Francisco, US',
    timestamp: new Date().toISOString(),
    status: 'SUCCESS',
    details: 'Initiated account deletion and revoked all user data and credentials',
  };

  globalIdentityState = {
    ...globalIdentityState,
    user: {
      ...globalIdentityState.user,
      status: 'PENDING_DELETION',
      isGuest: true,
    },
    savedCalculations: [],
    scenarios: [],
    favorites: [],
    alerts: [],
    passkeys: [],
    securityEvents: [securityEvent, ...globalIdentityState.securityEvents],
  };

  notifyListeners();
}

// -------------------------------------------------------------
// GUEST-TO-ACCOUNT MIGRATION
// -------------------------------------------------------------

export function migrateGuestDataToAccount(guestCalcs: SavedCalculation[]): void {
  if (!guestCalcs || guestCalcs.length === 0) return;

  const existingIds = new Set(globalIdentityState.savedCalculations.map((c) => c.id));
  const newCalcs = guestCalcs.filter((c) => !existingIds.has(c.id));

  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: [...newCalcs, ...globalIdentityState.savedCalculations],
  };

  notifyListeners();
}

// -------------------------------------------------------------
// RBAC & ADMIN COMMAND CENTER
// -------------------------------------------------------------

export function assignInternalRole(
  userEmail: string,
  userName: string,
  role: InternalRole,
  department: string
): RoleAssignment {
  const newAssignment: RoleAssignment = {
    id: `ra_${Date.now()}`,
    userEmail,
    userName,
    role,
    assignedAt: new Date().toISOString(),
    assignedBy: globalIdentityState.user.primaryEmail,
    department,
  };

  const newAuditLog: AdminAuditLogEntry = {
    id: `audit_${Date.now()}`,
    actorEmail: globalIdentityState.user.primaryEmail,
    action: 'ROLE_ASSIGNED',
    targetResource: `RoleAssignment: ${userEmail}`,
    details: `Assigned internal role "${role}" to ${userName} (${department})`,
    timestamp: new Date().toISOString(),
    ip: '198.51.100.42',
  };

  globalIdentityState = {
    ...globalIdentityState,
    roleAssignments: [newAssignment, ...globalIdentityState.roleAssignments],
    adminAuditLogs: [newAuditLog, ...globalIdentityState.adminAuditLogs],
  };

  notifyListeners();
  return newAssignment;
}

// -------------------------------------------------------------
// YAMOOPAY ACTIVITY EVENT LOGGING
// -------------------------------------------------------------

export function recordYamooPayActivity(
  event: Omit<YamooPayActivityItem, 'id' | 'userId' | 'timestamp'> & { timestamp?: string }
): YamooPayActivityItem {
  const newActivity: YamooPayActivityItem = {
    id: `act_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    userId: globalIdentityState.user.id,
    timestamp: event.timestamp || new Date().toISOString(),
    ...event,
  };

  globalIdentityState = {
    ...globalIdentityState,
    activities: [newActivity, ...globalIdentityState.activities],
  };

  notifyListeners();
  return newActivity;
}

export function clearYamooPayActivities(): void {
  globalIdentityState = {
    ...globalIdentityState,
    activities: [],
  };
  notifyListeners();
}

// -------------------------------------------------------------
// SAVED CALCULATIONS MANAGEMENT
// -------------------------------------------------------------

export function renameSavedCalculation(id: string, newTitle: string): void {
  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: globalIdentityState.savedCalculations.map((item) =>
      item.id === id
        ? { ...item, title: newTitle, updatedAt: new Date().toISOString() }
        : item
    ),
  };
  recordYamooPayActivity({
    type: 'SAVED_CALCULATION',
    category: 'CALCULATIONS',
    title: 'Renamed Calculation',
    subtitle: `Updated title to "${newTitle}"`,
    group: 'TODAY',
    hasDrilldown: false,
  });
  notifyListeners();
}

export function duplicateSavedCalculation(id: string): SavedCalculation | null {
  const target = globalIdentityState.savedCalculations.find((item) => item.id === id);
  if (!target) return null;

  const duplicated: SavedCalculation = {
    ...target,
    id: `calc_dup_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    title: `${target.title} (Copy)`,
    timestamp: Date.now(),
    createdAt: new Date().toISOString(),
  };

  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: [duplicated, ...globalIdentityState.savedCalculations],
  };

  recordYamooPayActivity({
    type: 'SAVED_CALCULATION',
    category: 'CALCULATIONS',
    title: 'Duplicated Calculation',
    subtitle: `Created copy: ${duplicated.title}`,
    resultFormatted: duplicated.primaryResult,
    group: 'TODAY',
    hasDrilldown: false,
  });

  notifyListeners();
  return duplicated;
}

export function archiveSavedCalculation(id: string): void {
  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: globalIdentityState.savedCalculations.map((item) =>
      item.id === id ? { ...item, isArchived: !item.isArchived } : item
    ),
  };
  notifyListeners();
}

export function deleteSavedCalculation(id: string): void {
  const target = globalIdentityState.savedCalculations.find((item) => item.id === id);
  globalIdentityState = {
    ...globalIdentityState,
    savedCalculations: globalIdentityState.savedCalculations.filter((item) => item.id !== id),
  };
  if (target) {
    recordYamooPayActivity({
      type: 'SAVED_CALCULATION',
      category: 'CALCULATIONS',
      title: 'Deleted Calculation',
      subtitle: `Removed "${target.title}"`,
      group: 'TODAY',
      hasDrilldown: false,
    });
  }
  notifyListeners();
}


